import { Card, CardContent } from '@mui/material';

// eslint-disable-next-line @nx/enforce-module-boundaries
import { routeCreator as referralProgramRouterCreator } from '@occc/feat-referral-program';
import { ToolboxContainer, ToolboxItem, ToolboxItemType } from '@occc-app/design-system';
import { Typography } from '@occc/ui-design-system';
import { routeCreator as awardRouteCreator } from '@occc-app/routes/AwardCatalog/utils';
import { routeCreator as productGroupsRouteCreator } from '@occc-app/routes/ProductGroup/utils';
import { routeCreator as displayCategoryRouteCreator } from '@occc-app/routes/DisplayCategory/utils';
import { routeCreator as loyaltyEventRouteCreator } from '@occc-app/routes/LoyaltyEvent/utils';
import { routeCreator as productCategoryRouteCreator } from '@occc-app/routes/ProductCategory/utils';
import { programManagementGameRouteCreator as gameRouteCreator } from '@occc-app/routes/Game/utils';
import { Feature, useIsUserPermittedTo } from '@occc/utils-access-control';
import { useChainConfiguration } from '@occc-app/Chain/hooks';
import { useConfig } from '@occc/config';
import { StoredValueToolboxItem } from '@occc/feat-stored-value';

export const ProgramManagement = () => {
  const isUserPermittedToCreateProductGroup = useIsUserPermittedTo(Feature.PRODUCT_GROUP_CREATE);
  const isUserPermittedToCreateAward = useIsUserPermittedTo(Feature.AWARD_CREATE);
  const isUserPermittedToCreateDisplayCategory = useIsUserPermittedTo(
    Feature.DISPLAY_CATEGORY_CREATE,
  );
  const isUserPermittedToCreateLoyaltyEvent = useIsUserPermittedTo(Feature.LOYALTY_EVENT_CREATE);
  const isUserPermittedToCreateProductCategory = useIsUserPermittedTo(
    Feature.PRODUCT_CATEGORY_CREATE,
  );
  const isUserPermittedToCreateGame = useIsUserPermittedTo(Feature.GAME_CREATE);
  const { referralProgramConfigurationEnabled } = useChainConfiguration();
  const { config } = useConfig();

  return (
    <Card>
      <CardContent>
        <Typography sx={{ mb: 3 }} variant='subtitle1'>
          Start by setting up your Products and Awards
        </Typography>
        <ToolboxContainer>
          <ToolboxItem
            title='Product Groups'
            description='Add your products here to use them anywhere in the Command Center'
            type={ToolboxItemType.ProductGroups}
            linkToCreate={
              isUserPermittedToCreateProductGroup ? productGroupsRouteCreator.create() : undefined
            }
            linkToDetails={productGroupsRouteCreator.list()}
          />
          <ToolboxItem
            title='Award Catalog'
            description='Add a set of Award you can use to award your member’s behavior'
            type={ToolboxItemType.AwardCatalog}
            linkToCreate={isUserPermittedToCreateAward ? awardRouteCreator.create() : undefined}
            linkToDetails={awardRouteCreator.base()}
          />
          <ToolboxItem
            title='Display Categories'
            description='Manage the Campaign categories and preferences available to your members in Experience'
            type={ToolboxItemType.DisplayCategory}
            linkToCreate={
              isUserPermittedToCreateDisplayCategory
                ? displayCategoryRouteCreator.create()
                : undefined
            }
            linkToDetails={displayCategoryRouteCreator.list()}
          />
          <ToolboxItem
            title='Loyalty Event Offers'
            description='Manage the behavioral offers in your loyalty program'
            type={ToolboxItemType.LoyaltyEvents}
            linkToCreate={
              isUserPermittedToCreateLoyaltyEvent ? loyaltyEventRouteCreator.create() : undefined
            }
            linkToDetails={loyaltyEventRouteCreator.base()}
          />
          <ToolboxItem
            title='Product Preferences'
            description='Manage product preferences that your members can express interest in'
            type={ToolboxItemType.ProductCategory}
            linkToCreate={
              isUserPermittedToCreateProductCategory
                ? productCategoryRouteCreator.create()
                : undefined
            }
            linkToDetails={productCategoryRouteCreator.list()}
          />
          <ToolboxItem
            title='Game Catalog'
            description='Create a game to engage with members within Experience'
            type={ToolboxItemType.VoucheredGames}
            linkToCreate={isUserPermittedToCreateGame ? gameRouteCreator.create() : undefined}
            linkToDetails={gameRouteCreator.base()}
          />
          {referralProgramConfigurationEnabled && (
            <ToolboxItem
              title='Referrals'
              description='Incentivize members to refer their friends'
              type={ToolboxItemType.ReferralProgram}
              linkToDetails={referralProgramRouterCreator.details()}
            />
          )}
          {config.storedValue.enabled && <StoredValueToolboxItem />}
        </ToolboxContainer>
      </CardContent>
    </Card>
  );
};
